import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { SimpleAlert, toAbsoluteUrl } from '../../../../_mexablock/helpers';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { getAvisoImageUrl, updNotice } from '../../../service/avisos/avisos';
import { ConfirmModal } from '../../../../_mexablock/helpers/components/ConfirmationModal';
import { WarModal } from '../modals/WarModal';

type Props = {
  num_aviso: number;
  title: string;
  show_app: boolean;
  description: string;
  image: string;
  file: string;
  id_aviso: number;
  date: string;
  handleReload: (updateGrid: boolean) => void;
};

const AvisosSchema = Yup.object().shape({
  title: Yup.string().required('Titulo es requerido.'),
  description: Yup.string().required('Descripción es requerida.'),
});

const AvisosCard: React.FC<Props> = ({
  num_aviso,
  title,
  show_app,
  description,
  image,
  file,
  id_aviso,
  date,
  handleReload,
}) => {
  const history = useHistory();
  const [showWar, setshowWar] = useState<boolean>(false);
  const [unblock, setUnblock] = useState<null | (() => void)>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [dataInventario, setDataInventario] = useState<any[]>([]);
  const [fileSize, setFileSize] = useState<number>(0);
  const [fileName, setFileName] = useState<string | null>(null);
  const [show, setShow] = useState<boolean>(false);
  const [pendingPath, setPendingPath] = useState<string | null>(null);
  const MAX_FILE_SIZE_MB = 15;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

  const formAvisos = useFormik({
    initialValues: {
      title: title || '',
      show_app: show_app || false,
      description: description || '',
      image: image,
      file: file || null,
      id_aviso: id_aviso || 0,
      date: date,
      is_current: 1,
    },
    validationSchema: AvisosSchema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      setLoading(true);
      try {
        const res = await updNotice(values);
        if (res?.data?.status === 'success') {
          const msg = res?.data?.message || 'Aviso actualizado con éxito';
          SimpleAlert(msg, 3000, 'success');
          handleReload(true);
          resetForm();
        }
      } catch (err: any) {
        const errorMsg =
          err?.response?.data?.message ||
          'Ocurrió un error al actualizar el aviso';
        SimpleAlert(errorMsg, 3000, 'error');
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  const handleFile = async (file: File) => {
    if (file.size > MAX_FILE_SIZE_BYTES) {
      alert(`El archivo supera los ${MAX_FILE_SIZE_MB} MB permitidos.`);
      return;
    }

    setFileName(file.name);
    setFileSize(file.size);
  };

  const ALLOWED_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const MAX_FILE_SIZE_MB = 5;
      const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

      if (file.size > MAX_FILE_SIZE_BYTES) {
        SimpleAlert(
          'El archivo excede los 5 MB. No se puede procesar',
          3000,
          'error'
        );
        e.target.value = '';
        return;
      }

      if (!ALLOWED_TYPES.includes(file.type)) {
        SimpleAlert(
          'Formato no permitido. Solo PDF, JPG, PNG y DOCX.',
          3000,
          'error'
        );
        e.target.value = '';
        return;
      }
      setFileName(file.name);
      setFileSize(file.size);
      setDataInventario([file]);
      formAvisos.setFieldValue('file', file);
      formAvisos.setFieldValue('is_current', 1);
      handleFile(file);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        SimpleAlert(
          'Formato no permitido. Solo PDF, JPG, PNG y DOCX.',
          3000,
          'error'
        );
        return;
      }
      handleFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  const fetchPost = async () => {
    setLoading(true);
    try {
      if (image) {
        const imageInit = await getAvisoImageUrl(image);
        if (imageInit) setPreviewImage(imageInit);
      }
      if (file) {
        setDataInventario([{ name: file }]);
        setFileName(file);
      }
    } catch (error) {
      console.error('Error fetching post:', error);
    }
  };

  const getFileIcon = (fileName: string | null): string => {
    if (!fileName) return toAbsoluteUrl('/media/svg/files/upload1.svg');

    const ext = fileName.split('.').pop()?.toLowerCase();

    switch (ext) {
      case 'pdf':
        return toAbsoluteUrl('/media/svg/files/pdf.svg');
      case 'docx':
        return toAbsoluteUrl('/media/svg/files/doc.svg');
      case 'jpg':
      case 'jpeg':
      case 'png':
        return toAbsoluteUrl('/media/svg/files/image.svg');
      default:
        return toAbsoluteUrl('/media/svg/files/upload1.svg');
    }
  };

  useEffect(() => {
    fetchPost();
  }, [image, file]);

  useEffect(() => {
    if (formAvisos.dirty && !formAvisos.isSubmitting) {
      const unblockFn = history.block((tx) => {
        setshowWar(true);
        setPendingPath(tx.pathname);
        return false;
      });
      setUnblock(() => unblockFn);

      return () => {
        unblockFn();
      };
    }
  }, [formAvisos.dirty, formAvisos.isSubmitting, history]);

  return (
    <>
      <div className="row gy-5 g-xl-8">
        <div className="col-xl-12">
          <div className="card card-custom mb-5">
            <div className="card-header cursor-pointer">
              <div className="card-title m-0">
                <h3 className="fw-bolder m-0 text-marine">Aviso {num_aviso}</h3>
              </div>
              <div
                className="card-toolbar"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                data-bs-trigger="hover"
              >
                <a
                  className="btn btn-sm btn-primary"
                  id="kt_toolbar_primary_button"
                  onClick={() => setShow(true)}
                >
                  Guardar
                </a>
              </div>
            </div>
            <div className="card-body py-3 mt-7">
              <div className="row" style={{ marginBottom: '60px' }}>
                <div className="col-5">
                  <div className="image-input " data-kt-image-input="true">
                    <img
                      src={
                        previewImage
                          ? previewImage
                          : toAbsoluteUrl('/media/avatars/defaultimg.jpg')
                      }
                      className="image-input-wrapper bg-secondary"
                      style={{
                        height: '305px',
                        width: '100%',
                        objectFit: 'cover',
                      }}
                      crossOrigin="anonymous"
                      alt="imagen aviso"
                    />
                    <label
                      className="btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow"
                      data-kt-image-input-action="change"
                      data-bs-toggle="tooltip"
                      title="Change avatar"
                    >
                      <i className="bi bi-pencil-fill fs-7"></i>
                      <input
                        type="file"
                        name="avatar"
                        accept=".png, .jpg, .jpeg"
                        onChange={(event) => {
                          if (event.currentTarget.files) {
                            let file = event.currentTarget.files[0];
                            const MAX_FILE_SIZE_MB = 5;
                            const MAX_FILE_SIZE_BYTES =
                              MAX_FILE_SIZE_MB * 1024 * 1024;
                            if (file.size > MAX_FILE_SIZE_BYTES) {
                              SimpleAlert(
                                'El peso de la imagen del producto debe ser de un máximo de 5MB',
                                3000,
                                'error'
                              );
                              return;
                            }
                            let reader = new FileReader();
                            reader.onload = () => {
                              const csv: string = reader.result as string;
                              setPreviewImage(csv);
                            };
                            reader.readAsDataURL(file);
                            formAvisos.setFieldValue('image', file);
                          }
                        }}
                      />
                    </label>
                    <span
                      className="btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow"
                      data-kt-image-input-action="cancel"
                      data-bs-toggle="tooltip"
                      title="Cancel avatar"
                    >
                      <i className="bi bi-x fs-2"></i>
                    </span>
                  </div>
                  <div className="text-muted fs-7 mt-7 text-center">
                    *La imagen deberá ser en formato <span>JPG</span> o{' '}
                    <span className="fw-bolder">PNG</span>, con resolución
                    recomendada de{' '}
                    <span className="fw-bolder">800x526 pixeles</span> y un{' '}
                    <span className="fw-bolder">peso máximo de 5 MB</span>.
                  </div>

                  <div
                    className="card mt-6 position-relative"
                    style={{ backgroundColor: '#f5f8fa' }}
                  >
                    {fileName && (
                      <button
                        type="button"
                        className="btn btn-sm btn-icon btn-circle btn-danger position-absolute top-0 end-0"
                        style={{ transform: 'translate(30%, -30%)' }}
                        onClick={() => {
                          setFileName(null);
                          setFileSize(0);
                          setDataInventario([]);
                          formAvisos.setFieldValue('file', null);
                          formAvisos.setFieldValue('is_current', 0);
                          if (inputRef.current) inputRef.current.value = '';
                        }}
                      >
                        <i className="bi bi-x fs-4"></i>
                      </button>
                    )}

                    <div
                      className="d-flex justify-content-center text-center flex-column p-8 h-200px overflow-hidden"
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onClick={handleClick}
                    >
                      <input
                        type="file"
                        accept=".pdf, .jpg, .jpeg, .png, .docx"
                        onChange={handleFileChange}
                        ref={inputRef}
                        style={{ display: 'none' }}
                      />
                      <a className="text-gray-800 text-hover-primary d-flex flex-column">
                        <div className="symbol symbol-75px mb-6">
                          <img
                            src={getFileIcon(fileName)}
                            alt="icono archivo"
                          />
                        </div>
                        <div className="fs-8 fw-bolder mb-2">
                          {dataInventario.length
                            ? `${fileName}`
                            : 'Puedes subir tu documento en .PDF, .DOCX, .JPG y .PNG'}
                        </div>
                      </a>
                      <div className="fs-9 fw-bold text-gray-500 mt-2">
                        {fileSize > 0
                          ? `Peso del archivo: ${(
                              fileSize /
                              (1024 * 1024)
                            ).toFixed(2)} MB`
                          : '* El documento deberá tener un peso máximo de 5 MB.'}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-7">
                  <div className="row">
                    <div className="col-12 mb-7">
                      <div className="w-100 d-flex">
                        <label className="fw-bold fs-6 mb-2 required">
                          Titulo
                        </label>
                      </div>
                      <input
                        maxLength={50}
                        value={formAvisos.values.title}
                        onChange={(e) =>
                          formAvisos.setFieldValue('title', e.target.value)
                        }
                        className="form-control form-control-solid mb-3"
                      />
                      {formAvisos.touched.title && formAvisos.errors.title && (
                        <div className="fv-plugins-message-container text-danger  ms-5">
                          <span role="alert">{formAvisos.errors.title}</span>
                        </div>
                      )}
                    </div>
                    <div className="col mb-7">
                      <div className="w-100 d-flex">
                        <label className="fw-bold fs-6 mb-2 required">
                          Fecha de publicación
                        </label>
                      </div>

                      <input
                        readOnly
                        value={formAvisos.values.date}
                        type="date"
                        className="form-control form-control-solid mb-3"
                      />
                    </div>
                    <div className="col mb-7">
                      <div className="w-100 d-flex">
                        <label className="fw-bold fs-6 mb-2">
                          Mostrar aviso en la APP
                        </label>
                      </div>
                      <div className="form-check form-check-custom form-check-solid mt-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={formAvisos.values.show_app}
                          onChange={(e) =>
                            formAvisos.setFieldValue(
                              'show_app',
                              e.target.checked
                            )
                          }
                        />
                        <label className="form-check-label text-muted">
                          Activo / Inactivo
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="w-100 d-flex">
                    <label className="fw-bold fs-6 mb-2 required">
                      Descripción
                    </label>
                  </div>
                  <textarea
                    placeholder="Descripción"
                    className="form-control form-control-solid mb-3 mb-lg-0 h-350px"
                    maxLength={500}
                    value={formAvisos.values.description}
                    onChange={(e) =>
                      formAvisos.setFieldValue('description', e.target.value)
                    }
                  />
                  {formAvisos.touched.description &&
                    formAvisos.errors.description && (
                      <div className="fv-plugins-message-container text-danger  ms-5">
                        <span role="alert">
                          {formAvisos.errors.description}
                        </span>
                      </div>
                    )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        show={show}
        confirmValue={(confirm) => {
          setShow(false);
          if (confirm) {
            formAvisos.handleSubmit();
          }
        }}
        onClose={(close) => {
          if (close) {
            setShow(false);
          }
        }}
      />

      <WarModal
        show={showWar}
        confirmValue={(confirm) => {
          setshowWar(false);
          if (confirm) {
            formAvisos.resetForm();
            if (unblock) unblock();
            if (pendingPath) history.push(pendingPath);
          }
        }}
        onClose={() => setshowWar(false)}
      />
    </>
  );
};

export { AvisosCard };
